import { useCallback, useEffect, useMemo, useState } from "react"

import { getAttachments } from "../../Album/api/attachment.services.ts"
import { type Attachment } from "../../Album/types/attachment.types.ts"
import { getProjectDocuments } from "../../Project/api/project-documents.services.ts"
import { type ProjectDocument } from "../../Project/types/project-documents.types.ts"
import {
  mapAttachmentToFileItem,
  mapDocumentToFileItem
} from "../helpers/map-to-file-item.ts"
import { type FileItem, type FileRights } from "../types/file-item.types.ts"

const TRASH_PAGE_SIZE = 100

interface UseTrashArgs {
  projectId: string
  rights: FileRights
  /** Only fetches while the Trash nav is actually open — matches every other
   *  nav-scoped fetch in this feature (`useDocumentTypeOptions`, filters). */
  enabled: boolean
}

/** Trashed documents and attachments — deliberately its own fetch rather
 *  than a client-side filter over `useFilesData`'s result set (the way
 *  Favorites works): trashed rows are excluded server-side from every other
 *  query, so there is nothing in `useFilesData`'s state to filter down to. */
export function useTrash({ projectId, rights, enabled }: UseTrashArgs) {
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [documents, setDocuments] = useState<ProjectDocument[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)

  const load = useCallback(async () => {
    if (!projectId) return
    setIsLoading(true)
    const [attachmentsRes, documentsRes] = await Promise.all([
      getAttachments({ project: projectId, trashed: true }),
      getProjectDocuments({
        lead: projectId,
        page: 1,
        page_size: TRASH_PAGE_SIZE,
        trashed: true
      })
    ])
    if (attachmentsRes.ok && attachmentsRes.data) {
      setAttachments(
        Array.isArray(attachmentsRes.data)
          ? attachmentsRes.data
          : attachmentsRes.data.results
      )
    }
    if (documentsRes.ok && documentsRes.data) {
      setDocuments(documentsRes.data.results || [])
    }
    setIsLoading(false)
    setHasLoaded(true)
  }, [projectId])

  useEffect(() => {
    if (!enabled) return
    void load()
  }, [enabled, load])

  const fileItems = useMemo<FileItem[]>(() => {
    const items = [
      ...attachments.map((attachment) => mapAttachmentToFileItem(attachment, rights)),
      ...documents.map((document) => mapDocumentToFileItem(document, rights))
    ]
    // Most-recently-trashed first — what a user emptying or hunting for a
    // just-deleted file wants to see at the top.
    return items.sort((a, b) => ((a.deletedAt ?? "") < (b.deletedAt ?? "") ? 1 : -1))
  }, [attachments, documents, rights])

  return {
    fileItems,
    isLoading,
    hasLoaded,
    isEmpty: hasLoaded && fileItems.length === 0,
    reload: load
  }
}
